/**
 * Product media component
 * 
 * @module product-media
 * @version 1.0.0
 * @extends HTMLElement
*/

customElements.component = 'product-media';

 import Drift from 'drift-zoom';

 if (!customElements.get(customElements.component)) {
 
    class productMediaComponent extends HTMLElement {
 
        /**
          * Product media web component.
          * 
          * @constructor
        */
        constructor() {

            super();

            /**
             * Media element.
             * 
             * @var {Object}
            */
            this.media = this.querySelector('[data-media]');

            /**
             * Thumbnails element.
             * 
             * @var {Object}
            */
            this.thumbnails = this.querySelector('[data-thumbnail-slider]');

            this.initCells();
            this.initSlider();
            this.initEvents();
            this.initPhotoSwipeEvents();

            if (!this.thumbnails) return;

            this.thumbnails.querySelectorAll("[data-thumbnail-item]").forEach(element => {

                element.addEventListener('click', event => {

                    event.preventDefault();

                    let index = element.dataset.thumbnailIndex;

                    this.activeThumbnail(index);

                    index = parseInt(index);

                    this.slider.slideTo(index, 500);

                }, false);
            });
        }
 
         /**
          * Init media cells array to handle image visibility on change variant.
          * 
          * @returns {undefined}
          */
        initCells() {
 
            /**
             * Media cells array
             * 
             * @var {Array}
            */
            this.cells = [];

            /**
             * Number of visible cells in the media based of the variant selected.
             * 
             * @var {Array}
            */
            this.activeCells = 0;

            this.media.querySelectorAll('[data-media-item]').forEach(item => {

                let index = item.dataset.mediaItemIndex;

                this.cells[index] = {
                    index: parseInt(index),
                    status: true,
                    alt: item.dataset.mediaItem,
                    element: item
                };
            });
        }
 
         /**
          * Init media slider.
          * 
          * @returns {undefined}
        */
        initSlider() {
 
            this.querySelectorAll('.swiper-lazy-loading').forEach(element => {
                element.classList.remove('swiper-lazy-loading');
            });

            const swiperMediaPagination = this.media.querySelector('.swiper-media-pagination');
            const swiperScrollbar = this.media.querySelector('.swiper-scrollbar');
    
            let defaultOptions = {
                preloadImages: true,
                lazy: {
                    loadPrevNext: true,
                },
                autoHeight: true,
                pagination: {
                    el: swiperMediaPagination,
                    dynamicBullets: true
                },
                scrollbar: {
                    el: swiperScrollbar,
                },
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                },
                on: {
                    init: () => {

                        if(!this.thumbnails) return false;

                        this.items = this.thumbnails.querySelectorAll('[data-thumbnail-item]');

                        this.initThumbnails();
                        this.initDriftZoomEvent(0);
                    },
                    slideChange: index => {

                        if(!this.thumbnails) return false;

                        this.initDriftZoomEvent(index.activeIndex);
                        this.activeThumbnail(index.activeIndex);
                    },
                    beforeResize: () => {
                        this.slider.slideTo(0, 200);
                    }
                }
            }

            let options = (this.dataset.options) ? JSON.parse(this.dataset.options) : {};

            this.options = {...defaultOptions, ...options};

            this.slider = new Swiper(this.media, this.options);
        }
 
        /**
         * Init media events.
         * 
         * @returns {undefined}
        */
        initEvents() {

            // On variant change
            window.addEventListener('variant-change', event => {

                if (event.detail.isQuickviewTrigger) return;

                if(event.detail.product.id ==  parseInt(this.dataset.productMediaId)){
                    this.swapImages(event.detail.product, event.detail.variant);
                }
            });
        }

        /**
         * Init media thumbnails.
         * 
         * @returns {undefined}
        */
        initThumbnails() {

            const slidesPerView = this.thumbnails.dataset.slidesPerView;
            const productsSlide = this.thumbnails.querySelector('.product-media__product-thumbnails');
            const swiperButtonNext = this.thumbnails.querySelector('.swiper-button-next');
            const swiperButtonPrev = this.thumbnails.querySelector('.swiper-button-prev');

            this.slideThumbnails = new Swiper(productsSlide, {
                slidesPerView: slidesPerView,
                preloadImages: true,
                autoHeight: false,
                navigation: {
                    nextEl: swiperButtonNext,
                    prevEl: swiperButtonPrev,
                },
                spaceBetween: 8,
                on: {
                    beforeResize: () => {
                        this.slideThumbnails.slideTo(0, 200);
                    }
                }
            });

            //Mark the first element as active
            this.thumbnails.querySelector('[data-thumbnail-item]').classList.add("active");
        }

        /**
         * Remove all active classes.
         * 
         * @returns {undefined}
        */
        removeAllActiveClasses() {
            this.thumbnails.querySelectorAll('[data-thumbnail-item]').forEach((thumbnail) => {
                thumbnail.classList.remove("active");
            });
        }

        /**
         * Active thumbnail by index.
         * 
         * @param {integer} index Thumbnail index.
         * @returns {undefined}
        */
        activeThumbnail(index) {
            index = parseInt(index);

            if (isNaN(index) || !this.thumbnails) return;

            if(this.thumbnails.querySelector(`[data-thumbnail-index="${index}"]`)) {

                this.removeAllActiveClasses();
                this.slideThumbnails.slideTo(index, 500);
                this.slider.slideTo(index, 500);
                this.thumbnails.querySelector(`[data-thumbnail-index="${index}"]`).classList.add("active");
            }

        }
 
        /**
         * Swap media images by variant.
         * 
         * @param {object} product Product data.
         * @param {object} variant Variant data.
         * @returns {undefined}
        */
        swapImages(product, variant) {

            if (!this.thumbnails) return;

            const productColorOption = this.getColorOption(product);

            if (productColorOption && productColorOption.values.length <= 1) return;

            let colorHandle = this.getVariantColorHandle(product, variant);

            if (!colorHandle) {

                this.showAllThumbnails();
                this.activeThumbnail(this.activeCells[0]);

                return;
            }

            this.activeCells = [];

            this.cells.forEach(cell => {

                let thumbnail = this.getThumbnail(cell.index);

                if (!thumbnail) return;

                if (this.cellMatchesColor(cell, colorHandle)) {

                    thumbnail.classList.remove("hide");
                    this.activeCells.push(cell.index);

                } else {

                    thumbnail.classList.add("hide");
                }
            });

            if (!this.activeCells.length) {

                this.showAllThumbnails();
            }

            this.activeThumbnail(this.activeCells[0]);
        }

        /**
         * Get the thumbnail element of a media cell.
         * 
         * @param {integer} index Media cell index.
         * @returns {object|null} Thumbnail element.
        */
        getThumbnail(index) {

            return (this.thumbnails) ? this.thumbnails.querySelector(`[data-thumbnail-index="${index}"]`) : null;
        }

        /**
         * Show every thumbnail and mark them all as active.
         * 
         * @returns {undefined}
        */
        showAllThumbnails() {

            this.activeCells = [];

            this.cells.forEach(cell => {

                let thumbnail = this.getThumbnail(cell.index),
                    isSwatch = cell.alt && cell.alt.toLowerCase().includes('swatch');

                if (thumbnail) {
                    thumbnail.classList.toggle("hide", !!isSwatch);
                }

                if (!isSwatch) {
                    this.activeCells.push(cell.index);
                }
            });
        }

        /**
         * Check if a media cell belongs to a color.
         * 
         * @param {object} cell Media cell data.
         * @param {string} colorHandle Handleized color value.
         * @returns {boolean} Cell belongs to the color or not.
        */
        cellMatchesColor(cell, colorHandle) {

            if (!cell.alt) return false;

            let alt = cell.alt.toLowerCase();

            if (alt.includes('swatch')) return false;

            return this.getAltColorHandles(alt).includes(colorHandle);
        }

        /**
         * Get the color option of a product.
         * 
         * @param {object} product Product data.
         * @returns {object|undefined} Color option data.
        */
        getColorOption(product) {

            return (product.options || []).find(option => {
                return option.name && option.name.toLowerCase() === 'color';
            });
        }

        /**
         * Get the handleized color value of a variant.
         * 
         * Matching on the color option value instead of searching the variant
         * title avoids colors that are a substring of another one ("BLACK" vs
         * "BLACK-SILVER") resolving to the wrong media.
         * 
         * @param {object} product Product data.
         * @param {object} variant Variant data.
         * @returns {string|null} Handleized color value.
        */
        getVariantColorHandle(product, variant) {

            let option = this.getColorOption(product);

            if (!option || !variant) return null;

            let position = option.position || ((product.options || []).indexOf(option) + 1),
                value = variant['option' + position];

            return (value) ? window.theme.handleize(value) : null;
        }

        /**
         * Get the color handles referenced by an image alt.
         * 
         * Alts follow the "Product name / COLOR" convention, but a color can
         * itself hold a divisor ("Product name / BLACK/GOLD") and some alts carry
         * an extra segment ("Product name / BLACK / Front"), so both readings are
         * returned.
         * 
         * @param {string} alt Image alt in lower case.
         * @returns {Array} Handleized color values.
        */
        getAltColorHandles(alt) {

            let parts = alt.split('/');

            if (parts.length < 2) return [];

            return [
                window.theme.handleize(parts.slice(1).join('/').trim()),
                window.theme.handleize(parts[1].trim())
            ].filter(handle => handle);
        }
 
        /**
         * Destroy media slider.
         * 
         * @returns {undefined}
        */
        destroySlider() {

            this.slider.destroy();
        }
 
        /**
         * Disable image by media cell index.
         * 
         * @param {integer} index Media cell index.
         * @returns {undefined}
        */
        disableImage(index) {
            
            this.cells[index].status = false;

            let element = this.querySelector('[data-media-item-index="'+ index +'"]');

            if (element) {
                element.remove();
            }
        }
 
        /**
         * Enable image by media cell index.
         * 
         * @param {integer} index Media cell index.
         * @returns {undefined}
        */
        enableImage(index) {

            this.cells[index].status = true;

            if (!this.querySelector('[data-media-item-index="' + index + '"]')) {

                this.media.querySelector('[data-media-wrapper]').append(this.cells[index].element);
            }
        }
 
        /**
         * Show all media images.
         * 
         * @returns {undefined}
        */
        showAllImages() {

            // Disable all images
            this.cells.forEach((cell, index) => {
                this.disableImage(index);
            });

            // Display all images except swatches
            this.cells.forEach((cell, index) => {
                if (cell.alt && cell.alt.toLowerCase().includes('swatch')) {
                    this.disableImage(index);
                } else {
                    this.enableImage(index);
                }
            });
        }

        /**
         * Init PhotoSwipe events.
         * 
         * @returns {undefined}
        */
        initPhotoSwipeEvents() {
            // Init zoom image
            let zoomImage = document.querySelectorAll('[data-pswp-img]');

            this.initZoomImage(zoomImage);

            // On screen change
            window.addEventListener('screen-breakpoint', event => {
                this.initZoomImage(zoomImage);
            });
        }

        /**
        * Start zoom with PhotoSwipe gallery on product image
        * 
        * @param {object} zoomImage Images that will init zoom.
        * @returns {undefined}
        */

        initZoomImage(zoomImage) {
            if(window.screenMode == 'tablet' || window.screenMode == 'desktop'  || window.screenMode == 'desktop-large' ) {
                //active on tablet, desktop and desktop-large devices by clicking the zoom button
                let zoomList = document.querySelectorAll('[data-zoom]');

                if(zoomList){
                    zoomList.forEach((zoomBtn) => {

                        if(zoomBtn && !zoomBtn.classList.contains('zoom-active') ){
                            zoomBtn.classList.add('zoom-active');
        
                            //init the zoom PhotoSwipe starting with the selected image
                            zoomBtn.addEventListener('click', (event) => {
                                let indexImg = zoomBtn.dataset.zoomImgIndex;
                                this.initPhotoSwipe(zoomImage, indexImg);
                            });
                        }
                        
                    });
                }

            } else {

                // active on mobile by clicking the image
                zoomImage.forEach((element, index) => {
                    if (!element.classList.contains('zoom-active')) {
                        element.classList.add('zoom-active');

                        //init the zoom PhotoSwipe starting with the selected image
                        element.addEventListener('click', (event) => {
                            this.initPhotoSwipe(zoomImage, index);
                        });
                    }
                });
            }
        }

        /**
         * Init the PhotoSwipe
         * 
         * @param {object} zoomImage Images that will init PhotoSwipe.
         * @param {integer} index Image index where the PhotoSwipe will start.
         * @returns {undefined}
         */

        initPhotoSwipe(zoomImage, index) {

            let items = [];

            zoomImage.forEach((element) => {
                let img = { 
                    src: element.dataset.pswpSrc,
                    w: element.dataset.pswpWidth,
                    h: element.dataset.pswpHeight
                };
                items.push(img);
            });

            let pswpElement = document.querySelector('.pswp');

            let options = {
                tapToClose: true,
                fullscreenEl: true,
                shareEl: false,
                index: index
            };

            let gallery = new PhotoSwipe( pswpElement, window.PhotoSwipeUI_Default, items, options);

            gallery.init();

        }

        /**
         * Start zoom hover Drift Zoom on the current product image
         * 
         * @param {number} index current product image
         * @returns {undefined}
         */
        initDriftZoomEvent(index){
            let element = this.querySelector('[data-media-item-index="'+ index +'"]');
            let zoomImg = element.querySelector('.product__zoom-img');
            const zoomFactor = element.querySelector('[data-zoom-factor]');
            if( window.screenMode == 'desktop'  || window.screenMode == 'desktop-large' && zoomImg && zoomFactor){
                new Drift(zoomImg, {
                  paneContainer: element,
                  inlinePane: false,
                  zoomFactor: zoomFactor.dataset.zoomFactor,
                });
            }
        }
    }

    customElements.define(customElements.component, productMediaComponent);
}
